import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    ApiRequestService,
    CardComponent,
    DateUtilPipe,
    DialogComponent,
    DialogConfiguration,
    DialogService,
    MAT_ICON,
    TOAST_STATE,
    ToastmessageService,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { SUCCESSFULLY_REMOVED } from 'src/app/shared/adminconstants';
import { MediaQueryService } from 'src/app/shared/media-query.service';
import { DeletePartnerCommissionRequest } from './deletepartnercommissionrequest';
import { DeletePartnerOnSalePeriodRequest } from './deletepartneronsaleperiodrequest';
import { GetPartnerDetailsRequest } from './getpartnerdetailsrequest';
import { GetPartnerDetailsResponse } from './getpartnerdetailsresponse';
import { PartnerCommission } from './partnercommission';
import { PartnerOnSalePeriod } from './partneronsaleperiod';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-partnerdetails',
    templateUrl: './partnerdetails.component.html',
    styleUrls: ['./partnerdetails.component.scss'],
    standalone: true,
    imports: [DateUtilPipe, MatIconModule, RouterModule, CardComponent]
})
export class PartnerDetailsComponent implements OnInit {
    deletePartnerCommissionURI = '/admin/deletePartnerCommission';
    deletePartnerOnSalePeriodURI = '/admin/deletePartnerOnSalePeriod';
    getPartnerDetailsRequest!: GetPartnerDetailsRequest;
    getPartnerDetailsResponse!: GetPartnerDetailsResponse;
    getPartnerDetailsURI = '/admin/getPartnerDetails';
    readonly isMobile = inject(MediaQueryService).createMatchSignal('(max-width: 768px)');
    partnerId: number | null = null;
    partnerName: string | null = null;
    storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);
    private _cdr = inject(ChangeDetectorRef);

    private _route = inject(ActivatedRoute);

    private _router = inject(Router);

    private _location = inject(Location);

    private _apiRequestService = inject(ApiRequestService);

    private _toastMessageService = inject(ToastmessageService);

    private _translateService = inject(TranslationService);

    private _dialogService = inject(DialogService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else if (sessionStorage.getItem('token')) {
            this._route.queryParamMap.subscribe((params) => {
                const partnerId = params.get('partnerId') as unknown as number;
                const partnerName = params.get('partnerName') as string;

                if (partnerId) {
                    this.partnerId = partnerId;
                }

                if (partnerName) {
                    this.partnerName = partnerName;
                }
            });
            this._loadPartnerDetails(this.partnerId);
        }
    }

    onNavigateBack(): void {
        this._location.back();
    }

    onAddNewCommissionToPartner(): void {
        void this._router.navigate(['/addpartner'], {
            queryParams: {
                mode: 'addCommission',
                partnerName: this.partnerName
            }
        });
    }

    onAddNewOnSalePeriodToPartner(): void {
        void this._router.navigate(['/addpartner'], {
            queryParams: {
                mode: 'addOnSalePeriod',
                partnerName: this.partnerName
            }
        });
    }

    onEditPartnerCommissionDetails(commission: PartnerCommission): void {
        localStorage.setItem('commission', JSON.stringify(commission));
        void this._router.navigate(['/addpartner'], {
            queryParams: {
                mode: 'editCommission',
                partnerName: this.partnerName
            }
        });
    }

    onRemoveCommissionClicked(commission: PartnerCommission): void {
        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'admin.dialog.confirmation.deleteCommissionMessage',
            targetData: commission.name
        };

        this._dialogService.openDialog(
            DialogComponent,
            dialogConfiguration,
            () => {
                this._removeCommission(commission);
            });
    }

    onEditPartnerOnSalePeriodDetails(onSalePeriod: PartnerOnSalePeriod): void {
        localStorage.setItem('onSalePeriod', JSON.stringify(onSalePeriod));
        void this._router.navigate(['/addpartner'], {
            queryParams: {
                mode: 'editOnSalePeriod',
                partnerName: this.partnerName
            }
        });
    }

    onRemoveOnSalePeriodClicked(onSalePeriod: PartnerOnSalePeriod): void {
        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'admin.dialog.confirmation.deleteOnSalePeriodMessage',
            targetData: onSalePeriod.name
        };

        this._dialogService.openDialog(
            DialogComponent,
            dialogConfiguration,
            () => {
                this._removeOnSalePeriod(onSalePeriod);
            });
    }

    private _loadPartnerDetails(partnerId: number | null): void {
        if (partnerId) {
            this.getPartnerDetailsRequest = { partnerId };
            this._apiRequestService
                .sendRequest(this.storeBaseUrl, this.getPartnerDetailsRequest, true, this.getPartnerDetailsURI)
                .subscribe((response) => {
                    const partnerDetails = response as GetPartnerDetailsResponse;
                    this.getPartnerDetailsResponse = { ...partnerDetails };
                    this._cdr.markForCheck();
                });
        } else {
            this._toastMessageService.displayToastMessage(TOAST_STATE.error, 'Missing partner ID!', MAT_ICON.error);
            void this._router.navigateByUrl('/partners');
        }
    }

    private _removeCommission(commission: PartnerCommission): void {
        const addPartnerRequest: DeletePartnerCommissionRequest = {
            commissionId: commission.commissionId
        };

        this._apiRequestService
            .sendRequest<DeletePartnerCommissionRequest, Void>(this.storeBaseUrl, addPartnerRequest, true, this.deletePartnerCommissionURI)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_REMOVED),
                    MAT_ICON.success
                );
                this._loadPartnerDetails(this.partnerId);
            });
    }

    private _removeOnSalePeriod(onSalePeriod: PartnerOnSalePeriod): void {
        const addPartnerRequest: DeletePartnerOnSalePeriodRequest = {
            onSalePeriodId: onSalePeriod.onSalePeriodId
        };

        this._apiRequestService
            .sendRequest<
                DeletePartnerOnSalePeriodRequest,
                Void
            >(this.storeBaseUrl, addPartnerRequest, true, this.deletePartnerOnSalePeriodURI)
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_REMOVED),
                    MAT_ICON.success
                );
                this._loadPartnerDetails(this.partnerId);
            });
    }
}
